/**
 * WorldCompleteScene — Phaser Scene displayed after completing all 5 caves in a world.
 *
 * Displays a full-screen victory image (1280×720) scaled to fit the viewport,
 * a "Voltar ao Mapa" button centered at the bottom, and marks the world
 * as complete in the progress tracker (localStorage).
 *
 * Requirements: 5.1, 5.3, 5.5
 */

import Phaser from 'phaser';

/** Data passed when starting WorldCompleteScene */
export interface WorldCompleteSceneData {
  mapId: string;
  completionImageKey: string; // preloaded texture key
}

/** localStorage key for storing completed world IDs */
const COMPLETED_WORLDS_KEY = 'awe_completed_worlds';

/** Button styling constants */
const BUTTON_WIDTH = 260;
const BUTTON_HEIGHT = 56;
const BUTTON_CORNER_RADIUS = 14;
const BUTTON_COLOR = 0x4caf50;
const BUTTON_HOVER_COLOR = 0x66bb6a;
const BUTTON_TEXT_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  fontFamily: 'Arial, sans-serif',
  fontSize: '24px',
  color: '#ffffff',
  fontStyle: 'bold',
};

export class WorldCompleteScene extends Phaser.Scene {
  private mapId!: string;
  private completionImageKey!: string;

  constructor() {
    super({ key: 'WorldCompleteScene' });
  }

  init(data: WorldCompleteSceneData): void {
    this.mapId = data.mapId;
    this.completionImageKey = data.completionImageKey;
  }

  create(): void {
    const { width, height } = this.scale;

    // Mark world as complete in progress tracker
    this.markWorldComplete(this.mapId);

    // Display full-screen victory image (1280×720), scaled to fit viewport
    this.createVictoryImage(width, height);

    // Show "Voltar ao Mapa" button centered at bottom
    this.createBackToMapButton(width, height);
  }

  /**
   * Displays the victory image scaled to fit the viewport while maintaining aspect ratio.
   */
  private createVictoryImage(viewportWidth: number, viewportHeight: number): void {
    const image = this.add.image(viewportWidth / 2, viewportHeight / 2, this.completionImageKey);

    // Scale image to fit viewport while maintaining aspect ratio
    const scaleX = viewportWidth / image.width;
    const scaleY = viewportHeight / image.height;
    const scale = Math.min(scaleX, scaleY);
    image.setScale(scale);
    image.setDepth(0);
  }

  /**
   * Creates the "Voltar ao Mapa" button centered at the bottom of the screen.
   */
  private createBackToMapButton(viewportWidth: number, viewportHeight: number): void {
    const buttonX = viewportWidth / 2;
    const buttonY = viewportHeight - 60;

    // Button background
    const buttonBg = this.add.graphics();
    buttonBg.fillStyle(BUTTON_COLOR, 1);
    buttonBg.fillRoundedRect(
      buttonX - BUTTON_WIDTH / 2,
      buttonY - BUTTON_HEIGHT / 2,
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
      BUTTON_CORNER_RADIUS
    );
    buttonBg.setDepth(1);

    // Button text
    const buttonText = this.add
      .text(buttonX, buttonY, 'Voltar ao Mapa', BUTTON_TEXT_STYLE)
      .setOrigin(0.5, 0.5)
      .setDepth(2);

    // Interactive hit area
    const hitArea = this.add
      .rectangle(buttonX, buttonY, BUTTON_WIDTH, BUTTON_HEIGHT, 0xffffff, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(3);

    // Hover effects
    hitArea.on('pointerover', () => {
      buttonBg.clear();
      buttonBg.fillStyle(BUTTON_HOVER_COLOR, 1);
      buttonBg.fillRoundedRect(
        buttonX - BUTTON_WIDTH / 2,
        buttonY - BUTTON_HEIGHT / 2,
        BUTTON_WIDTH,
        BUTTON_HEIGHT,
        BUTTON_CORNER_RADIUS
      );
    });

    hitArea.on('pointerout', () => {
      buttonBg.clear();
      buttonBg.fillStyle(BUTTON_COLOR, 1);
      buttonBg.fillRoundedRect(
        buttonX - BUTTON_WIDTH / 2,
        buttonY - BUTTON_HEIGHT / 2,
        BUTTON_WIDTH,
        BUTTON_HEIGHT,
        BUTTON_CORNER_RADIUS
      );
    });

    // Button tap: transition to MapSelectScene
    hitArea.on('pointerdown', () => {
      this.scene.start('MapSelectScene');
    });
  }

  /**
   * Marks the world as complete in localStorage.
   * Adds the mapId to the awe_completed_worlds array if not already present.
   */
  private markWorldComplete(mapId: string): void {
    try {
      const stored = localStorage.getItem(COMPLETED_WORLDS_KEY);
      let completedWorlds: string[] = [];

      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          completedWorlds = parsed;
        }
      }

      if (!completedWorlds.includes(mapId)) {
        completedWorlds.push(mapId);
        localStorage.setItem(COMPLETED_WORLDS_KEY, JSON.stringify(completedWorlds));
      }
    } catch (e) {
      console.warn('[WorldCompleteScene] Failed to save world completion:', e);
    }
  }
}
